import React, { useEffect, useState } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects]   = useState([]);
  const [managers, setManagers]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // 'active' | 'inactive' | 'all'
  const [showAdd, setShowAdd]     = useState(false);
  const [showAssign, setShowAssign] = useState(null);
  const [saving, setSaving]       = useState(false);
  const [form, setForm] = useState({
    employee_code:'', name:'', email:'', password:'',
    department:'', joining_date:'', manager_id:''
  });
  const [assignForm, setAssignForm] = useState({ project_id:'', role_on_project:'' });

  const load = () => {
    setLoading(true);
    Promise.all([api.get('/employees'), api.get('/projects'), api.get('/managers')])
      .then(([e,p,m]) => {
        setEmployees(e.data.employees);
        setProjects(p.data.projects);
        setManagers(m.data.managers);
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // Filter
  const filtered = employees
    .filter(e => {
      if (statusFilter === 'active')   return e.is_active;
      if (statusFilter === 'inactive') return !e.is_active;
      return true;
    })
    .filter(e =>
      !search ||
      [e.name, e.email, e.department||'', e.employee_code||''].join(' ')
        .toLowerCase().includes(search.toLowerCase())
    );

  const counts = {
    all:      employees.length,
    active:   employees.filter(e => e.is_active).length,
    inactive: employees.filter(e => !e.is_active).length,
  };

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleAdd = async (ev) => {
    ev.preventDefault(); setSaving(true);
    try {
      await api.post('/employees', form);
      toast.success('Employee onboarded!');
      setShowAdd(false);
      setForm({ employee_code:'', name:'', email:'', password:'', department:'', joining_date:'', manager_id:'' });
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed.'); }
    finally { setSaving(false); }
  };

  const handleAssign = async (ev) => {
    ev.preventDefault(); setSaving(true);
    try {
      await api.post(`/employees/${showAssign.id}/assign`, assignForm);
      toast.success('Project assigned!'); setShowAssign(null); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed.'); }
    finally { setSaving(false); }
  };

  // Toggle active/inactive
  const handleToggleStatus = async (emp) => {
    const action = emp.is_active ? 'Deactivate' : 'Activate';
    if (!window.confirm(`${action} ${emp.name}?`)) return;
    try {
      const res = await api.patch(`/employees/${emp.id}/toggle-status`);
      toast.success(res.data.message);
      load();
    } catch { toast.error('Failed to update status.'); }
  };

  const handleRemoveProject = async (empId, projId, projName) => {
    if (!window.confirm(`Remove from "${projName}"?`)) return;
    try { await api.delete(`/employees/${empId}/assign/${projId}`); toast.success('Removed.'); load(); }
    catch { toast.error('Failed.'); }
  };

  return (
    <div>
      {/* ── Header ── */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Employees</h1>
          <p className="page-sub">{counts.all} total · {counts.active} active · {counts.inactive} inactive</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ Onboard Employee</button>
      </div>

      {/* ── Filters ── */}
      <div style={{ display:'flex', gap:10, alignItems:'center', marginBottom:16, flexWrap:'wrap' }}>
        {/* Status tabs */}
        <div className="filter-tabs" style={{ marginBottom:0 }}>
          {[['all','All'], ['active','Active'], ['inactive','Inactive']].map(([val, lbl]) => (
            <button key={val} className={`filter-tab ${statusFilter===val?'active':''}`} onClick={() => setStatusFilter(val)}>
              {lbl} <span style={{ marginLeft:4, fontSize:11, opacity:.7 }}>({counts[val]})</span>
            </button>
          ))}
        </div>

        <input
          className="search-box"
          placeholder="Search name, email, ID, department…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex:1, minWidth:200, maxWidth:380 }}
        />
      </div>

      {loading ? (
        <div className="loading">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="card"><div className="empty"><div className="empty-icon">👥</div><p>No employees found.</p></div></div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {filtered.map(emp => (
            <div key={emp.id} className="card" style={{ padding:'16px 20px', opacity: emp.is_active ? 1 : 0.75 }}>
              <div style={{ display:'flex', alignItems:'flex-start', gap:14 }}>

                {/* Avatar */}
                <div className="avatar" style={{ width:42, height:42, fontSize:15, flexShrink:0 }}>
                  {emp.name.charAt(0)}
                </div>

                {/* Main info */}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                    <span style={{ fontWeight:600, fontSize:14 }}>{emp.name}</span>
                    {emp.employee_code && (
                      <span style={{ fontSize:11.5, color:'#6c63ff', background:'rgba(108,99,255,.12)', padding:'2px 8px', borderRadius:20, fontWeight:600 }}>
                        #{emp.employee_code}
                      </span>
                    )}
                    <span className={`badge ${emp.is_active ? 'badge-green' : 'badge-red'}`}>
                      {emp.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div style={{ color:'#6b7280', fontSize:12.5, marginTop:2 }}>{emp.email}</div>
                  <div style={{ display:'flex', gap:14, marginTop:6, flexWrap:'wrap' }}>
                    {emp.department && <span className="text-sm text-muted">🏢 {emp.department}</span>}
                    {emp.joining_date && <span className="text-sm text-muted">📅 {format(new Date(emp.joining_date),'MMM d, yyyy')}</span>}
                    {emp.reportingManager && <span className="text-sm text-muted">👤 {emp.reportingManager.name}</span>}
                  </div>

                  {/* Assigned projects */}
                  {emp.projects?.length > 0 && (
                    <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:10 }}>
                      {emp.projects.map(p => (
                        <span key={p.id} className="chip">
                          📁 {p.name}
                          {emp.is_active && (
                            <button
                              onClick={() => handleRemoveProject(emp.id,p.id,p.name)}
                              style={{ background:'none', border:'none', color:'#9ca3af', cursor:'pointer', fontSize:11, padding:'0 0 0 2px', lineHeight:1 }}
                            >✕</button>
                          )}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Action buttons */}
                <div style={{ display:'flex', gap:7, flexShrink:0, flexWrap:'wrap', justifyContent:'flex-end' }}>
                  {emp.is_active && (
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => { setShowAssign(emp); setAssignForm({ project_id:'', role_on_project:'' }); }}
                    >
                      Assign Project
                    </button>
                  )}
                  {/* Toggle active / inactive */}
                  <button
                    className={`btn btn-sm ${emp.is_active ? 'btn-danger' : 'btn-success'}`}
                    onClick={() => handleToggleStatus(emp)}
                  >
                    {emp.is_active ? 'Deactivate' : '✓ Activate'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Onboard Modal ── */}
      {showAdd && (
        <div className="overlay" onClick={() => setShowAdd(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">Onboard New Employee</div>
            <form onSubmit={handleAdd} className="form-grid">

              {/* Employee ID — mandatory, first field */}
              <div className="field">
                <label>Employee ID * <span style={{ fontSize:11, color:'#6b7280', fontWeight:400 }}>(e.g. 7628)</span></label>
                <input
                  required
                  value={form.employee_code}
                  onChange={e => setForm({...form, employee_code: e.target.value.trim()})}
                  placeholder="7628"
                  style={{ fontFamily:'monospace', fontWeight:600, letterSpacing:1 }}
                />
              </div>

              <div className="form-row">
                <div className="field">
                  <label>Full Name *</label>
                  <input required value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="Prakash Reddy"/>
                </div>
                <div className="field">
                  <label>Email *</label>
                  <input type="email" required value={form.email} onChange={e=>setForm({...form,email:e.target.value})} placeholder="prakash@company.com"/>
                </div>
              </div>

              <div className="form-row">
                <div className="field">
                  <label>Password *</label>
                  <input type="password" required minLength={6} value={form.password} onChange={e=>setForm({...form,password:e.target.value})} placeholder="Min 6 characters"/>
                </div>
                <div className="field">
                  <label>Department</label>
                  <input value={form.department} onChange={e=>setForm({...form,department:e.target.value})} placeholder="Engineering"/>
                </div>
              </div>

              <div className="form-row">
                <div className="field">
                  <label>Joining Date</label>
                  <input type="date" value={form.joining_date} onChange={e=>setForm({...form,joining_date:e.target.value})}/>
                </div>
                <div className="field">
                  <label>Reporting Manager</label>
                  <select value={form.manager_id} onChange={e=>setForm({...form,manager_id:e.target.value})}>
                    <option value="">Select manager…</option>
                    {managers.map(m=><option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Onboarding…' : 'Onboard Employee'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Assign Project Modal ── */}
      {showAssign && (
        <div className="overlay" onClick={() => setShowAssign(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">Assign Project — {showAssign.name}</div>
            <form onSubmit={handleAssign} className="form-grid">
              <div className="field">
                <label>Project *</label>
                <select required value={assignForm.project_id} onChange={e=>setAssignForm({...assignForm,project_id:e.target.value})}>
                  <option value="">Select project…</option>
                  {projects.filter(p=>p.status==='active').map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="field">
                <label>Role on Project</label>
                <input value={assignForm.role_on_project} onChange={e=>setAssignForm({...assignForm,role_on_project:e.target.value})} placeholder="e.g. Frontend Developer"/>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAssign(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Assigning…' : 'Assign'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}